import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, AtSign, Mail, Lock, Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { checkUsernameAvailability } from '../../lib/auth';
import { useToast } from '../ui/Toast';
import { Input } from '../ui/Input';
import { AvatarUpload } from './AvatarUpload';

const USERNAME_REGEX = /^[a-z0-9_]{3,20}$/;

export const ProfilePanel: React.FC = () => {
  const { user, updateProfile, updateEmail, updatePassword } = useAuth();
  const { showToast } = useToast();

  // --- Profile info state ---
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'invalid'>('idle');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const usernameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Email state ---
  const [newEmail, setNewEmail] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  // --- Password state ---
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // Sync state with user changes
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setUsername(user.username || '');
    }
  }, [user]);

  // --- Username validation with debounce ---
  const checkUsername = useCallback((value: string) => {
    if (usernameTimerRef.current) {
      clearTimeout(usernameTimerRef.current);
    }

    if (!value) {
      setUsernameStatus('idle');
      return;
    }

    if (!USERNAME_REGEX.test(value)) {
      setUsernameStatus('invalid');
      return;
    }

    // Skip check if unchanged
    if (value === user?.username) {
      setUsernameStatus('idle');
      return;
    }

    setUsernameStatus('checking');
    usernameTimerRef.current = setTimeout(async () => {
      const { available, error } = await checkUsernameAvailability(value, user?.id);
      if (error) console.warn('Username check failed:', error);
      setUsernameStatus(available ? 'available' : 'unavailable');
    }, 500);
  }, [user?.username]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(value);
    checkUsername(value);
  };

  // --- Save profile (name + username) ---
  const handleSaveProfile = async () => {
    if (usernameStatus === 'unavailable' || usernameStatus === 'invalid' || usernameStatus === 'checking') return;

    setIsSavingProfile(true);
    const updates: Record<string, string> = {};

    if (fullName !== (user?.full_name || '')) {
      updates.full_name = fullName;
    }

    if (username && username !== (user?.username || '')) {
      updates.username = username;
    } else if (!username && user?.username) {
      // Clear username is not allowed by CHECK constraint (min 3 chars)
      // so we just skip it
    }

    if (Object.keys(updates).length === 0) {
      showToast('info', 'Nenhuma alteracao detectada');
      setIsSavingProfile(false);
      return;
    }

    const { error } = await updateProfile(updates);

    if (error) {
      if (error.includes('chk_profiles_username_format')) {
        showToast('error', 'Username deve ter 3-20 caracteres (letras, numeros, _)');
      } else if (error.includes('idx_profiles_username_unique')) {
        showToast('error', 'Username indisponivel');
      } else {
        showToast('error', error);
      }
    } else {
      showToast('success', 'Perfil atualizado');
      setUsernameStatus('idle');
    }

    setIsSavingProfile(false);
  };

  // --- Save email ---
  const handleSaveEmail = async () => {
    if (!newEmail || newEmail === user?.email) return;

    setIsSavingEmail(true);
    const { error } = await updateEmail(newEmail);

    if (error) {
      showToast('error', error);
    } else {
      showToast('success', 'Email atualizado. Verifique sua caixa de entrada para confirmar.');
      setNewEmail('');
    }

    setIsSavingEmail(false);
  };

  // --- Save password ---
  const handleSavePassword = async () => {
    if (newPassword.length < 6) return;
    if (newPassword !== confirmPassword) return;

    setIsSavingPassword(true);
    const { error } = await updatePassword(newPassword);

    if (error) {
      showToast('error', error);
    } else {
      showToast('success', 'Senha atualizada');
      setNewPassword('');
      setConfirmPassword('');
    }

    setIsSavingPassword(false);
  };

  // --- Avatar update ---
  const handleAvatarUpdated = async (newUrl: string) => {
    const { error } = await updateProfile({ avatar_url: newUrl });
    if (error) {
      showToast('error', 'Erro ao salvar foto de perfil');
    } else {
      showToast('success', 'Foto de perfil atualizada');
    }
  };

  const profileHasChanges =
    fullName !== (user?.full_name || '') ||
    (username !== (user?.username || '') && username !== '');

  const passwordsMatch = newPassword === confirmPassword;
  const passwordValid = newPassword.length >= 6;

  const usernameHint = (() => {
    switch (usernameStatus) {
      case 'checking': return 'Verificando...';
      case 'available': return 'Disponivel';
      case 'unavailable': return 'Indisponivel';
      case 'invalid': return 'Use 3-20 caracteres: letras, numeros, _';
      default: return 'Seu identificador unico (ex: moabe)';
    }
  })();

  const usernameHintColor = (() => {
    switch (usernameStatus) {
      case 'available': return 'text-green-400';
      case 'unavailable': case 'invalid': return 'text-red-400';
      case 'checking': return 'text-amber-400';
      default: return 'text-neutral-500';
    }
  })();

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Meu Perfil</h1>
        <p className="text-neutral-400 mt-1">Gerencie sua conta e informacoes pessoais</p>
      </div>

      {/* Avatar Section */}
      <div className="liquid-glass rounded-2xl p-6 mb-6 flex flex-col items-center">
        <AvatarUpload
          currentAvatarUrl={user.avatar_url}
          userId={user.id}
          onAvatarUpdated={handleAvatarUpdated}
        />
      </div>

      {/* Profile Info Section */}
      <div className="liquid-glass rounded-2xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Informacoes</h2>
        <div className="space-y-4">
          <Input
            label="Nome de exibicao"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Seu nome"
            leftIcon={<User className="w-4 h-4" />}
          />

          <div>
            <Input
              label="Username"
              value={username}
              onChange={handleUsernameChange}
              placeholder="seu_username"
              leftIcon={<AtSign className="w-4 h-4" />}
              rightIcon={
                usernameStatus === 'checking' ? <Loader2 className="w-4 h-4 animate-spin text-amber-400" /> :
                usernameStatus === 'available' ? <Check className="w-4 h-4 text-green-400" /> :
                usernameStatus === 'unavailable' || usernameStatus === 'invalid' ? <AlertCircle className="w-4 h-4 text-red-400" /> :
                undefined
              }
            />
            <p className={`mt-1 text-xs ${usernameHintColor}`}>
              {usernameHint}
            </p>
          </div>

          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={isSavingProfile || !profileHasChanges || usernameStatus === 'unavailable' || usernameStatus === 'invalid' || usernameStatus === 'checking'}
            className="w-full px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSavingProfile ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar informacoes'
            )}
          </button>
        </div>
      </div>

      {/* Email Section */}
      <div className="liquid-glass rounded-2xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Email</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Email atual</label>
            <p className="text-white text-sm">{user.email}</p>
          </div>

          <Input
            label="Novo email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="novo@email.com"
            leftIcon={<Mail className="w-4 h-4" />}
          />

          <button
            type="button"
            onClick={handleSaveEmail}
            disabled={isSavingEmail || !newEmail || newEmail === user.email}
            className="w-full px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSavingEmail ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Atualizando...
              </>
            ) : (
              'Alterar email'
            )}
          </button>
        </div>
      </div>

      {/* Password Section */}
      <div className="liquid-glass rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Senha</h2>
        <div className="space-y-4">
          <Input
            label="Nova senha"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min. 6 caracteres"
            leftIcon={<Lock className="w-4 h-4" />}
            error={newPassword.length > 0 && newPassword.length < 6 ? 'Minimo 6 caracteres' : undefined}
          />

          <Input
            label="Confirmar senha"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repita a senha"
            leftIcon={<Lock className="w-4 h-4" />}
            error={confirmPassword.length > 0 && !passwordsMatch ? 'Senhas nao conferem' : undefined}
          />

          <button
            type="button"
            onClick={handleSavePassword}
            disabled={isSavingPassword || !passwordValid || !passwordsMatch}
            className="w-full px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSavingPassword ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Atualizando...
              </>
            ) : (
              'Alterar senha'
            )}
          </button>
        </div>
      </div>

      {/* Footer */}
      <p className="text-center text-[11px] text-neutral-600 mt-8 mb-4">
        Developed by{' '}
        <a href="https://vecta.id" target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-neutral-400 transition-colors">
          Vecta
        </a>
        {' '}<span className="text-neutral-700">v{__APP_VERSION__}</span>
      </p>
    </div>
  );
};
